import localforage from "localforage";
import { useState, useCallback, useRef, useEffect } from "react";
import { requestPersistentOfflineStorage } from "../offline/offlineStore";

const tileStore = localforage.createInstance({
  name: "GeoSahaOfflineTiles",
  storeName: "tiles",
});

const paftaTileIndexStore = localforage.createInstance({
  name: "GeoSahaOfflineTiles",
  storeName: "pafta_tile_index",
});

const tileRefCountStore = localforage.createInstance({
  name: "GeoSahaOfflineTiles",
  storeName: "tile_ref_count",
});

const MAX_PREFETCH_TILE_COUNT = 10000;
const MAX_TRACKED_TILE_PER_PAFTA = 12000;
const PREFETCH_CHUNK_SIZE = 10;
const PREFETCH_CHUNK_DELAY_MS = 150;

const paftaWriteLocks = new Map();
const tileWriteLocks = new Map();

function makePaftaKey(projectId, paftaId) {
  return `${projectId}::${paftaId}`;
}

function getProjectPrefix(projectId) {
  return `${projectId}::`;
}

function normalizePaftaIds(ids = []) {
  return [...new Set((ids || []).map((id) => String(id || "").trim()).filter(Boolean))];
}

function runWithPaftaLock(paftaKey, task) {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request(`geo-saha:pafta:${paftaKey}`, task);
  }

  const previous = paftaWriteLocks.get(paftaKey) || Promise.resolve();
  const next = previous.then(task, task);

  paftaWriteLocks.set(paftaKey, next);
  next.finally(() => {
    if (paftaWriteLocks.get(paftaKey) === next) {
      paftaWriteLocks.delete(paftaKey);
    }
  });

  return next;
}

function runWithTileLock(tileUrl, task) {
  const lockName = `geo-saha:tile:${tileUrl}`;
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request(lockName, task);
  }

  const previous = tileWriteLocks.get(tileUrl) || Promise.resolve();
  const next = previous.then(task, task);
  tileWriteLocks.set(tileUrl, next);
  return next.finally(() => {
    if (tileWriteLocks.get(tileUrl) === next) tileWriteLocks.delete(tileUrl);
  });
}

async function releaseTileUrl(tileUrl) {
  return runWithTileLock(tileUrl, async () => {
    const refCount = Number((await tileRefCountStore.getItem(tileUrl)) || 0);

    if (refCount <= 1) {
      await tileRefCountStore.removeItem(tileUrl);
      await tileStore.removeItem(tileUrl);
      return 1;
    }

    await tileRefCountStore.setItem(tileUrl, refCount - 1);
    return 0;
  });
}

async function releaseTileUrls(tileUrls = []) {
  let removedTileCount = 0;
  for (const tileUrl of tileUrls) {
    removedTileCount += await releaseTileUrl(tileUrl);
  }
  return removedTileCount;
}

export function useTileCache() {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const objectUrlsRef = useRef(new Set());
  const isDownloadingRef = useRef(false);
  const isPrefetchingRef = useRef(false);

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    isDownloadingRef.current = isDownloading;
  }, [isDownloading]);

  useEffect(() => {
    isPrefetchingRef.current = isPrefetching;
  }, [isPrefetching]);

  const getTileCoordinates = (bounds, minZoom, maxZoom) => {
    const tiles = [];
    const { _southWest: sw, _northEast: ne } = bounds;

    for (let z = minZoom; z <= maxZoom; z++) {
      const lat2tile = (lat, zoom) =>
        Math.floor(
          ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) /
            2) *
            Math.pow(2, zoom)
        );
      const lon2tile = (lon, zoom) =>
        Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));

      const top = lat2tile(ne.lat, z);
      const bottom = lat2tile(sw.lat, z);
      const left = lon2tile(sw.lng, z);
      const right = lon2tile(ne.lng, z);

      const minY = Math.min(top, bottom);
      const maxY = Math.max(top, bottom);

      for (let x = left; x <= right; x++) {
        for (let y = minY; y <= maxY; y++) {
          tiles.push({ x, y, z });
        }
      }
    }

    return tiles;
  };

  const trackTilesForPafta = useCallback(async (projectId, paftaId, tileUrls = []) => {
    if (!projectId || !paftaId || !tileUrls.length) return 0;

    const cleanedTileUrls = [...new Set(tileUrls.filter(Boolean))];
    if (!cleanedTileUrls.length) return 0;

    const paftaKey = makePaftaKey(projectId, paftaId);

    return runWithPaftaLock(paftaKey, async () => {
      const existingRaw = await paftaTileIndexStore.getItem(paftaKey);
      const existingTileUrls = Array.isArray(existingRaw) ? existingRaw : [];
      const existingSet = new Set(existingTileUrls);
      const remainingCapacity = Math.max(0, MAX_TRACKED_TILE_PER_PAFTA - existingTileUrls.length);

      if (remainingCapacity <= 0) return 0;

      const newTileUrls = cleanedTileUrls
        .filter((tileUrl) => !existingSet.has(tileUrl))
        .slice(0, remainingCapacity);

      if (!newTileUrls.length) return 0;

      await paftaTileIndexStore.setItem(paftaKey, [...existingTileUrls, ...newTileUrls]);

      for (const tileUrl of newTileUrls) {
        await runWithTileLock(tileUrl, async () => {
          const refCount = Number((await tileRefCountStore.getItem(tileUrl)) || 0);
          await tileRefCountStore.setItem(tileUrl, refCount + 1);
        });
      }

      return newTileUrls.length;
    });
  }, []);

  const trackTileForPaftas = useCallback(
    async (projectId, paftaIds, tileUrl) => {
      const normalizedPaftaIds = normalizePaftaIds(paftaIds);
      if (!projectId || !normalizedPaftaIds.length || !tileUrl) return;

      await Promise.all(
        normalizedPaftaIds.map((paftaId) =>
          trackTilesForPafta(projectId, paftaId, [tileUrl])
        )
      );
    },
    [trackTilesForPafta]
  );

  const syncProjectActivePaftaCache = useCallback(async (projectId, activePaftaIds = []) => {
    if (!projectId) {
      return { removedPaftaCount: 0, removedTileCount: 0 };
    }

    const prefix = getProjectPrefix(projectId);
    const activeSet = new Set(normalizePaftaIds(activePaftaIds));
    const keys = await paftaTileIndexStore.keys();
    const stalePaftaKeys = keys.filter((key) => {
      if (!key.startsWith(prefix)) return false;
      const paftaId = key.slice(prefix.length);
      return !activeSet.has(paftaId);
    });

    let removedTileCount = 0;
    for (const key of stalePaftaKeys) {
      const tileUrlsRaw = await paftaTileIndexStore.getItem(key);
      const tileUrls = Array.isArray(tileUrlsRaw) ? tileUrlsRaw : [];
      await paftaTileIndexStore.removeItem(key);
      removedTileCount += await releaseTileUrls(tileUrls);
    }

    return {
      removedPaftaCount: stalePaftaKeys.length,
      removedTileCount,
    };
  }, []);

  const downloadRegion = useCallback(
    async (
      bounds,
      minZoom = 12,
      maxZoom = 17,
      tileUrlTemplate,
      options = {}
    ) => {
      setIsDownloading(true);
      isDownloadingRef.current = true;
      setDownloadProgress(0);
      await requestPersistentOfflineStorage();

      const projectId = String(options?.projectId || "").trim() || null;
      const paftaIds = normalizePaftaIds(options?.paftaIds || []);
      const paftaToUrls = new Map(paftaIds.map((paftaId) => [paftaId, new Set()]));

      try {
        const tilesToDownload = getTileCoordinates(bounds, minZoom, maxZoom);
        const total = tilesToDownload.length;

        if (total === 0) {
          alert("İndirilecek bir alan bulunamadı.");
          return;
        }

        if (total > 5000) {
          const proceed = window.confirm(
            `Bu işlem çok büyük bir alanı kapsıyor (${total} görsel). Devam etmek istiyor musunuz?`
          );
          if (!proceed) return;
        }

        let completed = 0;
        const chunkSize = 10;

        for (let i = 0; i < total; i += chunkSize) {
          const chunk = tilesToDownload.slice(i, i + chunkSize);

          await Promise.all(
            chunk.map(async ({ x, y, z }) => {
              const url = tileUrlTemplate
                .replace("{z}", z)
                .replace("{x}", x)
                .replace("{y}", y);

              try {
                const exists = await tileStore.getItem(url);
                if (!exists) {
                  const response = await fetch(url);
                  if (response.ok) {
                    const blob = await response.blob();
                    await tileStore.setItem(url, blob);
                  } else {
                    console.warn(`Server ${response.status} döndü: ${url}`);
                  }
                }

                for (const paftaId of paftaIds) {
                  paftaToUrls.get(paftaId)?.add(url);
                }
              } catch (error) {
                console.warn(`Tile indirilemedi: ${url}`, error?.message || error);
              } finally {
                completed += 1;
              }
            })
          );

          setDownloadProgress(Math.round((completed / total) * 100));
        }

        if (projectId && paftaIds.length) {
          await Promise.all(
            paftaIds.map((paftaId) =>
              trackTilesForPafta(projectId, paftaId, [...(paftaToUrls.get(paftaId) || [])])
            )
          );
        }

        alert(`İndirme tamamlandı. ${total} tile çevrimdışı kullanım için kaydedildi.`);
      } catch (error) {
        console.error("Harita indirme hatası:", error);
        alert(`Harita indirilirken hata oluştu: ${error.message}`);
      } finally {
        setIsDownloading(false);
        isDownloadingRef.current = false;
        setDownloadProgress(0);
      }
    },
    [trackTilesForPafta]
  );

  const prefetchPoints = useCallback(
    async (
      points,
      radiusKm = 1.5,
      minZoom = 12,
      maxZoom = 18,
      tileUrlTemplate,
      projectId = null
    ) => {
      if (!points?.length || isDownloadingRef.current || isPrefetchingRef.current) return;

      const limitedPoints = points.slice(0, 500);
      const tileAssignments = new Map();

      for (const point of limitedPoints) {
        if (point?.lat == null || point?.lng == null) continue;

        const deg = radiusKm / 111;
        const bounds = {
          _southWest: { lat: point.lat - deg, lng: point.lng - deg },
          _northEast: { lat: point.lat + deg, lng: point.lng + deg },
        };

        const tiles = getTileCoordinates(bounds, minZoom, maxZoom);
        for (const tile of tiles) {
          const key = `${tile.z}/${tile.x}/${tile.y}`;
          let assignment = tileAssignments.get(key);

          if (!assignment) {
            assignment = {
              ...tile,
              paftaIds: new Set(),
            };
            tileAssignments.set(key, assignment);
          }

          if (projectId && point.paftaId) {
            assignment.paftaIds.add(String(point.paftaId));
          }
        }
      }

      const tilesArray = Array.from(tileAssignments.values()).slice(0, MAX_PREFETCH_TILE_COUNT);
      if (!tilesArray.length) return;

      setIsPrefetching(true);
      isPrefetchingRef.current = true;
      const paftaToUrls = new Map();

      try {
        for (let i = 0; i < tilesArray.length; i += PREFETCH_CHUNK_SIZE) {
          if (isDownloadingRef.current) break;

          const chunk = tilesArray.slice(i, i + PREFETCH_CHUNK_SIZE);
          await Promise.all(
            chunk.map(async ({ x, y, z, paftaIds }) => {
              const url = tileUrlTemplate
                .replace("{z}", z)
                .replace("{x}", x)
                .replace("{y}", y);

              try {
                const exists = await tileStore.getItem(url);
                if (!exists) {
                  const response = await fetch(url, { priority: "low" });
                  if (response.ok) {
                    const blob = await response.blob();
                    await tileStore.setItem(url, blob);
                  }
                }

                if (projectId && paftaIds?.size) {
                  for (const paftaId of paftaIds) {
                    if (!paftaToUrls.has(paftaId)) {
                      paftaToUrls.set(paftaId, new Set());
                    }
                    paftaToUrls.get(paftaId).add(url);
                  }
                }
              } catch (_error) {
                // Sessiz geç: prefetch ana akışı bozmasın.
              }
            })
          );

          await new Promise((resolve) => setTimeout(resolve, PREFETCH_CHUNK_DELAY_MS));
        }

        if (projectId && paftaToUrls.size > 0) {
          await Promise.all(
            [...paftaToUrls.entries()].map(([paftaId, tileUrlSet]) =>
              trackTilesForPafta(projectId, paftaId, [...tileUrlSet])
            )
          );
        }
      } catch (error) {
        console.error("[Auto-Download] Prefetch hatası:", error);
      } finally {
        setIsPrefetching(false);
        isPrefetchingRef.current = false;
      }
    },
    [trackTilesForPafta]
  );

  const clearCache = useCallback(async () => {
    if (!window.confirm("Çevrimdışı harita belleğini temizlemek istediğinize emin misiniz?")) {
      return;
    }

    try {
      await tileStore.clear();
      await paftaTileIndexStore.clear();
      await tileRefCountStore.clear();
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
      alert("Çevrimdışı harita belleği başarıyla temizlendi.");
    } catch (error) {
      console.error("Cache temizleme hatası:", error);
      alert("Bellek temizlenirken hata oluştu.");
    }
  }, []);

  const getCachedTile = useCallback(async (url) => {
    try {
      const blob = await tileStore.getItem(url);
      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        objectUrlsRef.current.add(objectUrl);
        return objectUrl;
      }
    } catch (error) {
      console.error("Önbellekten tile okunamadı:", error);
    }
    return null;
  }, []);

  const getTileSource = useCallback(
    async (url, projectId = null, paftaIds = []) => {
      const normalizedPaftaIds = normalizePaftaIds(paftaIds);
      const hasCacheContext = Boolean(projectId && normalizedPaftaIds.length);

      try {
        const existingBlob = await tileStore.getItem(url);
        if (existingBlob) {
          if (hasCacheContext) {
            await trackTileForPaftas(projectId, normalizedPaftaIds, url);
          }
          const objectUrl = URL.createObjectURL(existingBlob);
          objectUrlsRef.current.add(objectUrl);
          return objectUrl;
        }

        if (!hasCacheContext) {
          return null;
        }

        const response = await fetch(url);
        if (!response.ok) {
          return null;
        }

        const blob = await response.blob();
        await tileStore.setItem(url, blob);
        await trackTileForPaftas(projectId, normalizedPaftaIds, url);

        const objectUrl = URL.createObjectURL(blob);
        objectUrlsRef.current.add(objectUrl);
        return objectUrl;
      } catch (_error) {
        return null;
      }
    },
    [trackTileForPaftas]
  );

  return {
    isDownloading,
    isPrefetching,
    downloadProgress,
    downloadRegion,
    prefetchPoints,
    clearCache,
    getCachedTile,
    getTileSource,
    syncProjectActivePaftaCache,
    tileStore,
  };
}
